import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PlanetLoader from '../components/PlanetLoader';
import Background from '../components/Background';
import logger from '../utils/logger';

const AuthRedirect: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading, needsOnboarding } = useAuth();
  const [timeoutReached, setTimeoutReached] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const redirectAttemptedRef = useRef(false);

  useEffect(() => {
    const hashFragment = window.location.hash;
    logger.componentMount('AuthRedirect', { 
      hasUser: !!user, 
      isLoading, 
      needsOnboarding,
      hashLength: hashFragment?.length || 0,
      hashPreview: hashFragment ? hashFragment.substring(0, 50) + '...' : 'none'
    });

    // Set a timeout to prevent infinite loading (15 seconds)
    logger.info('AuthRedirect', 'Setting 15-second timeout');
    timeoutRef.current = setTimeout(() => {
      logger.warn('AuthRedirect', 'Timeout reached waiting for authentication', {
        hasUser: !!user,
        isLoading,
        needsOnboarding
      });
      setTimeoutReached(true);
    }, 15000);

    return () => {
      logger.componentUnmount('AuthRedirect');
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  useEffect(() => {
    logger.debug('AuthRedirect', 'Redirect logic effect triggered', {
      hasUser: !!user,
      isLoading,
      needsOnboarding,
      timeoutReached,
      redirectAttempted: redirectAttemptedRef.current
    });

    // Prevent multiple redirect attempts
    if (redirectAttemptedRef.current) {
      logger.debug('AuthRedirect', 'Redirect already attempted, skipping');
      return;
    }

    // Wait for loading to complete (unless timeout reached)
    if (isLoading && !timeoutReached) {
      logger.debug('AuthRedirect', 'Still loading, waiting...', { 
        hasUser: !!user,
        needsOnboarding,
        timeoutReached
      });
      return;
    }

    // If timeout reached and still no user, redirect to home
    if (timeoutReached && !user) {
      logger.warn('AuthRedirect', 'Timeout reached with no user, redirecting to home', {
        timeoutReached,
        hasUser: !!user,
        isLoading
      });
      redirectAttemptedRef.current = true;
      navigate('/', { replace: true });
      return;
    }

    // No user? Redirect to home
    if (!user) {
      logger.info('AuthRedirect', 'No user found, redirecting to home', {
        isLoading,
        timeoutReached
      });
      redirectAttemptedRef.current = true;
      navigate('/', { replace: true });
      return;
    }

    // User authenticated, check onboarding
    // If timeout reached, default to onboarding (safe default for new users)
    const shouldOnboard = timeoutReached || needsOnboarding;
    
    logger.info('AuthRedirect', 'User authenticated, determining redirect destination', {
      hasUser: !!user,
      needsOnboarding,
      timeoutReached,
      shouldOnboard,
      userEmail: user.email
    });
    
    if (shouldOnboard) {
      logger.info('AuthRedirect', 'User needs onboarding, redirecting to /onboarding', {
        timeoutReached,
        needsOnboarding,
        userEmail: user.email
      });
      redirectAttemptedRef.current = true;
      navigate('/onboarding', { replace: true });
    } else {
      logger.info('AuthRedirect', 'User authenticated and onboarded, redirecting to /user', {
        userEmail: user.email
      });
      redirectAttemptedRef.current = true;
      navigate('/user', { replace: true });
    }
  }, [user, isLoading, needsOnboarding, navigate, timeoutReached]);

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center" style={{ backgroundColor: '#050508' }}>
      <Background />
      <div className="relative z-10 flex flex-col items-center justify-center">
        <PlanetLoader />
        <p className="mt-8 text-text-secondary font-elegant tracking-luxury-tight">
          {isLoading ? 'Authenticating...' : 'Redirecting...'}
        </p>
      </div>
    </div>
  );
};

export default AuthRedirect;
